import React from 'react';

interface ReportHeaderProps {
  activeTab: string;
  onTabChange: (tab: 'general' | 'self-assessment' | 'grow' | 'evaluators' | 'leadership' | 'career' | 'collaborator') => void;
  visibleTabs?: string[];
}

export function ReportHeader({ 
  activeTab, 
  onTabChange,
  visibleTabs = ['general', 'self-assessment', 'evaluators', 'grow', 'leadership', 'career', 'collaborator']
}: ReportHeaderProps) {
  return (
    <div className="px-6 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Feedback Report</h1>
      <p className="text-gray-600 mb-6">
        This report compares your self-assessment with aggregated feedback from your evaluators.
      </p>

      <div className="flex space-x-6 border-b border-gray-200 overflow-x-auto overflow-y-hidden hide-scrollbar no-scrollbar">
        {visibleTabs.includes('general') && (
          <TabButton 
            label="General Results"
            isActive={activeTab === 'general'}
            onClick={() => onTabChange('general')}
          />
        )}
        {visibleTabs.includes('self-assessment') && (
          <TabButton 
            label="Self-assessment"
            isActive={activeTab === 'self-assessment'}
            onClick={() => onTabChange('self-assessment')} 
          />
        )}
        {visibleTabs.includes('evaluators') && (
          <TabButton 
            label="Evaluators"
            isActive={activeTab === 'evaluators'}
            onClick={() => onTabChange('evaluators')}
          />
        )}
        {visibleTabs.includes('grow') && (
          <TabButton 
            label="GROW"
            isActive={activeTab === 'grow'}
            onClick={() => onTabChange('grow')}
          />
        )}
        {visibleTabs.includes('leadership') && (
          <TabButton 
            label="Leadership Edge"
            isActive={activeTab === 'leadership'}
            onClick={() => onTabChange('leadership')}
          />
        )}
        {visibleTabs.includes('career') && (
          <TabButton 
            label="Career Accelerator"
            isActive={activeTab === 'career'}
            onClick={() => onTabChange('career')}
          />
        )}
        {visibleTabs.includes('collaborator') && (
          <TabButton 
            label="Collaborator Genius"
            isActive={activeTab === 'collaborator'}
            onClick={() => onTabChange('collaborator')}
          />
        )}
      </div>
    </div>
  );
}

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, isActive, onClick }: TabButtonProps) {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      className={`group px-4 py-2 font-medium text-sm transition-all duration-200 relative rounded-lg ${getTabStyle(label, isActive)}`}
    >
      {label}
      <div 
        className={`absolute bottom-0 left-0 right-0 h-0.5 transition-all duration-200 transform origin-left ${
          isActive
            ? getActiveBarStyle(label)
            : getHoverBarStyle(label)
        }`}
      />
      <div 
        className={`absolute -bottom-[2px] left-1/2 w-1.5 h-1.5 rounded-full transition-all duration-200 transform -translate-x-1/2 ${
          isActive
            ? getDotStyle(label)
            : 'bg-primary/50 scale-0 opacity-0'
        }`}
      />
    </button>
  );
}

function getTabStyle(label: string, isActive: boolean): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return isActive 
      ? 'text-[#f9c55f] bg-[#f9c55f]/5' 
      : 'text-gray-500 hover:text-gray-700';
  }
  return isActive 
    ? 'text-primary bg-primary/5' 
    : 'text-gray-500 hover:text-gray-700';
}

function getActiveBarStyle(label: string): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return 'bg-[#f9c55f] scale-x-100 opacity-100';
  }
  return 'bg-primary scale-x-100 opacity-100';
}

function getHoverBarStyle(label: string): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return 'bg-[#f9c55f]/50 scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-50';
  }
  return 'bg-primary/50 scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-50';
}

function getDotStyle(label: string): string {
  if (label === 'Leadership Edge' || 
      label === 'Career Accelerator' ||
      label === 'Collaborator Genius') {
    return 'bg-[#f9c55f] scale-100 opacity-100';
  }
  return 'bg-primary scale-100 opacity-100';
}